import OverviewLinksStatistics from "features/affiliation/Overview/OverviewLinksStatistics";
import TiersSummary from "features/affiliation/common/Tiers/TiersSummary";
import Balance from "features/affiliation/common/Balance";
import EarningsPieChart from "features/affiliation/common/Charts/EarningsPieChart";
import ReferredUsersBarChart from "features/affiliation/common/Charts/ReferredUsersBarChart";
import OverviewExistingUsersStatistics from "features/affiliation/Overview/OverviewExistingUsersStatistics";
import OverviewPayoutsStatistics from "features/affiliation/Overview/OverviewPayoutsStatistics";
import useOverview from "features/affiliation/common/hooks/useOverview";
import styled, { useTheme } from "styled-components";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import EarningsByMonthBarChart from "features/affiliation/common/Charts/EarningsByMonthBarChart";
import { useContext } from "react";
import { AffiliationContext } from "features/affiliation/Affiliation.context";
import useMedia from "utils/hooks/useMedia";
import { LARGE_MOBILE, TABLET } from "common/constants";
import { OVERVIEW_SMALL_SCREEN_LAYOUT } from "features/affiliation/common/constants";
import { phoneMaxWidth } from "common/NewLook/layout.ts";

const Overview = () => {
  const theme = useTheme();
  const { isAdmin } = useContext(AffiliationContext)!;
  const { isLoading } = useOverview();
  const isMobile = useMedia([`(max-width: ${LARGE_MOBILE})`], [true], false);
  const isTablet = useMedia([`(max-width: ${TABLET})`], [true], false);
  const isSmallScreen = useMedia(
    [`(max-width: ${OVERVIEW_SMALL_SCREEN_LAYOUT})`],
    [true],
    false
  );
  const showExistingUsers = isAdmin;

  return (
    <LoadingSpinnerOverlay
      isLoading={isLoading}
      hideContentWhileLoading={true}
      spinnerSize="large"
      spinnerMarginTop={150}
      backgroundColor={theme.colors.layoutBgColor}
      wrapStyle={{ height: "auto" }}
    >
      <LayoutWrapper
        $isSmallScreen={isSmallScreen}
        $isTablet={isTablet}
        $isMobile={isMobile}
      >
        <TiersWrapper $isMobile={isMobile}>
          <TiersSummary />
        </TiersWrapper>
        <BalanceWrapper $isSmallScreen={isSmallScreen} $isMobile={isMobile}>
          <Balance />
        </BalanceWrapper>
        <OverviewLinksStatistics />
        <BarChartWrapper
          $showExistingUsers={showExistingUsers}
          $isMobile={isMobile}
        >
          <EarningsByMonthBarChart inOverview={true} />
        </BarChartWrapper>
        <EarningsWrapper $isSmallScreen={isSmallScreen} $isMobile={isMobile}>
          <EarningsPieChart horizontal={!isSmallScreen} inOverview={true} />
        </EarningsWrapper>
        <ReferredChartWrapper>
          <ReferredUsersBarChart inOverview={true} />
        </ReferredChartWrapper>
        {showExistingUsers && <OverviewExistingUsersStatistics />}
        <PayoutsWrapper $isSmallScreen={isSmallScreen} $isMobile={isMobile}>
          <OverviewPayoutsStatistics />
        </PayoutsWrapper>
      </LayoutWrapper>
    </LoadingSpinnerOverlay>
  );
};

export default Overview;

const LayoutWrapper = styled.div<{
  $isTablet: boolean;
  $isSmallScreen: boolean;
  $isMobile: boolean;
}>`
  display: grid;
  gap: ${(p) => (p.$isTablet ? 12 : 16)}px;
  margin-top: 24px;
  grid-template-columns: ${(p) =>
    p.$isSmallScreen ? (p.$isMobile ? "1fr" : "1fr 50%") : "1fr 33% 1fr"};
  @media (max-width: ${phoneMaxWidth}) {
    margin-top: 16px;
  }
`;
const TiersWrapper = styled.div<{ $isMobile: boolean }>`
  grid-row: 1;
  grid-column: ${(p) => (p.$isMobile ? 1 : "1 / 3")};
`;
const BalanceWrapper = styled.div<{
  $isSmallScreen: boolean;
  $isMobile: boolean;
}>`
  ${(p) =>
    p.$isSmallScreen &&
    `
    grid-row: 6;
    grid-column: 2;
  `};
  ${(p) =>
    p.$isMobile &&
    `
     grid-row: 11;
     grid-column: 1;
  `};
`;
const BarChartWrapper = styled.div<{
  $showExistingUsers?: boolean;
  $isMobile: boolean;
}>`
  grid-row: 2 / ${(p) => (p.$showExistingUsers ? 5 : 6)};
  grid-column: ${(p) => (p.$isMobile ? 1 : "2 / 3")};

  ${(p) =>
    p.$isMobile &&
    `
    grid-row: 8 / 10;
  `}
`;
const ReferredChartWrapper = styled.div<{ $isMobile?: boolean }>`
  grid-row: 3 / ${(p) => (p.$isMobile ? 5 : 6)};
  grid-column: 1 / 2;
`;
const EarningsWrapper = styled.div<{
  $isSmallScreen: boolean;
  $isMobile?: boolean;
}>`
  grid-row: ${(p) =>
    p.$isSmallScreen ? (p.$isMobile ? "6 / 8" : "6 / 9") : "2 / 4"};
  grid-column: ${(p) => (p.$isSmallScreen ? 1 : 3)};
`;
const PayoutsWrapper = styled.div<{
  $isSmallScreen: boolean;
  $isMobile: boolean;
}>`
  grid-row: ${(p) =>
    p.$isSmallScreen ? (p.$isMobile ? 12 : "7 / 9") : "4 / 6"};
  grid-column: ${(p) => (p.$isSmallScreen ? (p.$isMobile ? 1 : 2) : 3)};
`;
